// The AI memo of a Case or a person, opened from the "…" of its screen. It is
// AI's working note, so the screen never shows it in the body.
//
// Promise: the handover is written in place (no frame) and saved when the
// field is left, against the version that was read; the log lists the newest
// first and each entry can be deleted, never rewritten.
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { m } from "@trameria/i18n";
import { useState } from "react";
import { useApi } from "../app/context";
import { apiErrorMessage } from "../data/errors";
import { useFormat } from "../me/profile";
import { RefText } from "../refs/ref-text";
import { ErrorLine } from "../ui/bits";
import { Button, IconButton } from "../ui/button";
import { Icon } from "../ui/icon";
import { Modal } from "../ui/modal";
import {
  type Memo,
  memoLogQuery,
  memoQuery,
  useDeleteMemoEntry,
  useSaveMemo,
} from "./queries";

const SECTION = "mb-2 text-[11px] tracking-[0.04em] text-faint";
const HANDOVER_MAX = 8000;

export function AgentMemoDialog({
  kind,
  id,
  open,
  onClose,
}: {
  kind: string;
  id: string;
  open: boolean;
  onClose(): void;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      label={m.memos_title()}
      className="!w-[min(640px,calc(100vw-32px))]"
    >
      {open && <MemoBody kind={kind} id={id} onClose={onClose} />}
    </Modal>
  );
}

function MemoBody({
  kind,
  id,
  onClose,
}: {
  kind: string;
  id: string;
  onClose(): void;
}) {
  const memo = useQuery(memoQuery(useApi(), kind, id));
  return (
    <div className="flex max-h-[70vh] min-h-0 flex-col">
      <div className="flex items-start gap-2">
        <h2 className="min-w-0 flex-1 text-[14px] font-normal text-fg">
          {m.memos_title()}
        </h2>
        <IconButton label={m.memos_close()} onClick={onClose}>
          <Icon kind="close" size={14} />
        </IconButton>
      </div>
      <div className="mt-3 min-h-0 overflow-y-auto">
        <div className={SECTION}>{m.memos_handover()}</div>
        {memo.data && <Handover kind={kind} id={id} memo={memo.data} />}
        {memo.isError && (
          <ErrorLine>
            {apiErrorMessage(memo.error, m.memos_load_failed())}
          </ErrorLine>
        )}
        <div className={`${SECTION} mt-6`}>{m.memos_log()}</div>
        <MemoLog kind={kind} id={id} />
      </div>
    </div>
  );
}

function Handover({
  kind,
  id,
  memo,
}: {
  kind: string;
  id: string;
  memo: Memo;
}) {
  // The text as first shown; later changes elsewhere do not replace what is
  // being written (the save is checked against the version read).
  const [draft, setDraft] = useState(memo.body);
  const save = useSaveMemo(kind, id);
  return (
    <>
      <textarea
        aria-label={m.memos_handover()}
        placeholder={m.memos_handover_placeholder()}
        value={draft}
        maxLength={HANDOVER_MAX}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (draft !== memo.body)
            save.mutate({ body: draft, version: memo.version });
        }}
        className="plain-field block min-h-[96px] w-full resize-none border-none bg-transparent p-0 text-[13.5px] leading-[1.7] text-body outline-none [field-sizing:content]"
      />
      {save.isError && (
        <ErrorLine>
          {apiErrorMessage(save.error, m.memos_save_failed())}
        </ErrorLine>
      )}
    </>
  );
}

function MemoLog({ kind, id }: { kind: string; id: string }) {
  const log = useInfiniteQuery(memoLogQuery(useApi(), kind, id));
  const remove = useDeleteMemoEntry(kind, id);
  const format = useFormat();
  const entries = log.data?.pages.flatMap((p) => p.entries) ?? [];
  if (log.isSuccess && entries.length === 0)
    return <p className="text-[12.5px] text-faint">{m.memos_log_empty()}</p>;
  return (
    <>
      {remove.isError && (
        <ErrorLine>
          {apiErrorMessage(remove.error, m.memos_log_delete_failed())}
        </ErrorLine>
      )}
      <ul className="flex flex-col gap-3">
        {entries.map((e) => (
          <li key={e.id} className="group relative pr-8">
            <div className="text-[11.5px] tabular-nums text-faint">
              {format.dateTime(e.createdAt)}
            </div>
            <RefText
              markdown={e.body}
              className="mt-0.5 text-[13px] leading-[1.6] text-body"
            />
            <IconButton
              label={m.memos_log_delete()}
              onClick={() => remove.mutate({ entryId: e.id })}
              className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 focus:opacity-100 pointer-coarse:opacity-100"
            >
              <Icon kind="close" size={12} />
            </IconButton>
          </li>
        ))}
      </ul>
      {log.hasNextPage && (
        <Button
          variant="ghost"
          className="mt-3"
          disabled={log.isFetchingNextPage}
          onClick={() => void log.fetchNextPage()}
        >
          {m.memos_log_more()}
        </Button>
      )}
    </>
  );
}
